import React, { useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import { Calculator } from "lucide-react";
import CustomInput from "../components/ui/CustomInput";
import PrimaryButton from "../components/ui/PrimaryButton";
import { BodySmallMedium, BodySmallRegular } from "../components/ui/Typography";
import { ReactComponent as UserIcon } from "../assets/ic_user.svg";
import { ReactComponent as HashIcon } from "../assets/ic_hash.svg";
import { ReactComponent as ArrowDown } from "../assets/ic_arrow_down.svg";
import {
  useCashInByPlayerAndAgentId,
  useCashInByPlayerId,
} from "../hooks/useCashExchange";
import { useVerifyCustomer } from "../hooks/useVerification";
import { useForexRates, useForexRate } from "../hooks/useForex";
import {
  CashInRequest,
  CashInRequestByPlayerAndAgentId,
} from "../api/services/cashExchange/type";
import {
  Agent,
  Customer,
} from "../api/services/cashExchange/verificationService";
import { ForexRate } from "../api/services/forex/type";
import { useSearchAgents } from "../hooks/useAgents";
import { AgentSearchResult } from "../api/services/agents/type";

interface CashInFormData {
  casinoPlayerId: string;
  agentId: string;
  assignAgent: boolean;
  paymentMethod: string;
  currency: string;
  cashAmount: number;
  exchangeRate: number;
  nprAmount: number;
}

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "credit", label: "Credit" },
];

const getCurrencySymbol = (currency: string): string => {
  const symbols: { [key: string]: string } = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    INR: "₹",
    NPR: "रू",
    CAD: "C$",
    AUD: "A$",
    CHF: "₣",
    CNY: "¥",
    SGD: "S$",
    HKD: "HK$",
    THB: "฿",
    MYR: "RM",
    SAR: "﷼",
    AED: "د.إ",
    QAR: "﷼",
    KWD: "د.ك",
    BHD: ".د.ب",
    OMR: "﷼",
    SEK: "kr",
    DKK: "kr",
    KRW: "₩",
  };
  return symbols[currency] || currency;
};

export const CashIn: React.FC = () => {
  const [playerVerified, setPlayerVerified] = useState(false);
  const [agentVerified, setAgentVerified] = useState(false);
  const [verifiedPlayer, setVerifiedPlayer] = useState<Customer | null>(null);
  const [verifiedAgent, setVerifiedAgent] = useState<Agent | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const cashInByPlayerIdMutation = useCashInByPlayerId();
  const cashInByPlayerAndAgentIdMutation = useCashInByPlayerAndAgentId();
  const verifyCustomerMutation = useVerifyCustomer();
  const { data: forexRatesData, isLoading: isLoadingForexRates } =
    useForexRates();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    control,
    setValue,
  } = useForm<CashInFormData>({
    defaultValues: {
      casinoPlayerId: "",
      agentId: "",
      assignAgent: false,
      paymentMethod: "cash",
      currency: "USD",
      cashAmount: 0,
      exchangeRate: 0,
      nprAmount: 0,
    },
  });

  const watchAssignAgent = watch("assignAgent");
  const watchCurrency = watch("currency");
  const watchPlayerId = watch("casinoPlayerId");
  const watchAgentId = watch("agentId");
  const watchExchangeRate = watch("exchangeRate");
  const watchCashAmount = watch("cashAmount");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { data: currentForexData } = useForexRate(watchCurrency);

  const availableCurrencies = React.useMemo(() => {
    const currencies = [];

    // Add NPR (Nepali Rupee) - no API call needed
    currencies.push({
      value: "NPR",
      label: "Nepali Rupee",
      symbol: getCurrencySymbol("NPR"),
    });

    // Add INR (Indian Rupee) - no API call needed
    currencies.push({
      value: "INR",
      label: "Indian Rupee",
      symbol: getCurrencySymbol("INR"),
    });

    // Add other currencies from API
    if (forexRatesData?.data && Array.isArray(forexRatesData.data)) {
      const apiCurrencies = (forexRatesData.data as ForexRate[])
        .filter((rate) => rate.currency !== "NPR" && rate.currency !== "INR") // Exclude NPR and INR from API
        .map((rate) => ({
          value: rate.currency,
          label: rate.currencyName,
          symbol: getCurrencySymbol(rate.currency),
        }));
      currencies.push(...apiCurrencies);
    }

    return currencies;
  }, [forexRatesData]);

  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);
    return debouncedValue;
  };

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data: agentsData, isLoading: isLoadingAgents } = useSearchAgents(
    debouncedSearchQuery,
    watchAssignAgent && !agentVerified,
  );

  React.useEffect(() => {
    if (verifiedPlayer && verifiedPlayer.playerId !== watchPlayerId) {
      setPlayerVerified(false);
      setVerifiedPlayer(null);
    }
  }, [watchPlayerId, verifiedPlayer]);

  React.useEffect(() => {
    if (verifiedAgent && verifiedAgent.agentId !== watchAgentId) {
      setAgentVerified(false);
      setVerifiedAgent(null);
    }
  }, [watchAgentId, verifiedAgent]);

  React.useEffect(() => {
    if (!watchAssignAgent) {
      setAgentVerified(false);
      setVerifiedAgent(null);
      setSearchQuery("");
    }
  }, [watchAssignAgent]);

  React.useEffect(() => {
    if (searchQuery && !verifiedAgent) {
      setAgentVerified(false);
      setVerifiedAgent(null);
    }
  }, [searchQuery, verifiedAgent]);

  useEffect(() => {
    if (
      agentsData &&
      agentsData.length > 0 &&
      searchQuery &&
      watchAssignAgent &&
      !agentVerified &&
      !isLoadingAgents
    ) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  }, [
    agentsData,
    searchQuery,
    watchAssignAgent,
    agentVerified,
    isLoadingAgents,
  ]);

  React.useEffect(() => {
    if (!watchAssignAgent && watch("paymentMethod") === "credit") {
      setValue("paymentMethod", "");
    }
  }, [watchAssignAgent, setValue, watch]);

  React.useEffect(() => {
    // If NPR is selected, set exchange rate to 1
    if (watchCurrency === "NPR") {
      setValue("exchangeRate", 1);
    }
    // If INR is selected, set exchange rate to 1 (since final amount is already in INR)
    else if (watchCurrency === "INR") {
      setValue("exchangeRate", 1);
    }
    // For other currencies, use API rate
    else if (currentForexData?.data && !Array.isArray(currentForexData.data)) {
      setValue("exchangeRate", currentForexData.data.sellRate);
    }
  }, [currentForexData, watchCurrency, setValue]);

  React.useEffect(() => {
    let finalAmount = 0;

    if (watchCurrency === "INR") {
      // If currency is already INR, the final amount is the same as cash amount
      finalAmount = watchCashAmount || 0;
    } else {
      // For NPR and other currencies, first convert to NPR
      const calculatedNpr = (watchCashAmount || 0) * (watchExchangeRate || 0);
      // Then convert NPR to INR (1 INR = 1.6 NPR, so divide by 1.6)
      finalAmount = calculatedNpr / 1.6;
    }

    // Round to 2 decimal places
    const roundedInr = Math.round(finalAmount * 100) / 100;

    // Set the final amount in INR
    setValue("nprAmount", roundedInr);
  }, [watchCashAmount, watchExchangeRate, watchCurrency, setValue]);

  const onSubmit: SubmitHandler<CashInFormData> = async (data) => {
    try {
      if (!playerVerified) {
        toast.error("Please verify the casino customer ID first");
        return;
      }

      if (data.assignAgent && !agentVerified) {
        toast.error("Please verify the agent ID first");
        return;
      }

      if (data.assignAgent) {
        const cashInData: CashInRequestByPlayerAndAgentId = {
          amount: data.nprAmount,
          playerId: data.casinoPlayerId,
          paymentType: data.paymentMethod,
          agentCode: data.agentId,
          currency: data.currency,
        };
        await cashInByPlayerAndAgentIdMutation.mutateAsync(cashInData);
      } else {
        const cashInData: CashInRequest = {
          amount: data.nprAmount,
          playerId: data.casinoPlayerId,
          paymentType: data.paymentMethod,
          currency: data.currency,
        };
        await cashInByPlayerIdMutation.mutateAsync(cashInData);
      }

      reset({
        casinoPlayerId: "",
        agentId: "",
        assignAgent: false,
        paymentMethod: "cash",
        currency: "USD",
        cashAmount: 0,
      });
      setPlayerVerified(false);
      setAgentVerified(false);
      setVerifiedPlayer(null);
      setVerifiedAgent(null);
    } catch (error) {
      console.error("Cash in submission error:", error);
    }
  };

  const handlePlayerVerify = async () => {
    const playerId = watch("casinoPlayerId");
    if (playerId.trim()) {
      try {
        const response = await verifyCustomerMutation.mutateAsync(playerId);
        setPlayerVerified(true);
        setVerifiedPlayer(response.data);
      } catch {
        setPlayerVerified(false);
        setVerifiedPlayer(null);
      }
    } else {
      toast.error("Please enter a player ID first");
    }
  };

  const handleAgentSelect = (agent: AgentSearchResult) => {
    setValue("agentId", agent.agentId);
    setVerifiedAgent(agent as Agent);
    setAgentVerified(true);
    setIsDropdownOpen(false);
    setSearchQuery(`${agent.name} (${agent.agentId})`);
  };

  const handleDeselectAgent = () => {
    setAgentVerified(false);
    setVerifiedAgent(null);
    setValue("agentId", "");
    setSearchQuery("");
  };

  const isLoading = isSubmitting || cashInByPlayerIdMutation.isPending;
  const isVerifyingPlayer = verifyCustomerMutation.isPending;

  return (
    <div className="p-6 bg-slate-900 min-h-screen">
      <div className="max-w-4xl">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Casino Player ID */}
          <div>
            <BodySmallRegular className="text-gray-300 mb-2">
              Customer ID*
            </BodySmallRegular>
            <div className="flex gap-3">
              <div className="flex-1">
                <CustomInput
                  icon={<UserIcon className="w-5 h-5 text-slate-400" />}
                  placeholder="Enter customer ID"
                  type="text"
                  name="casinoPlayerId"
                  register={register}
                  validation={{
                    required: "Casino Player ID is required",
                  }}
                  error={errors.casinoPlayerId}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              <button
                type="button"
                onClick={handlePlayerVerify}
                disabled={isVerifyingPlayer || isLoading}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  playerVerified
                    ? "bg-green-600 text-white"
                    : "bg-slate-600 hover:bg-slate-500 text-white disabled:bg-slate-700 disabled:text-slate-400"
                }`}
              >
                {isVerifyingPlayer
                  ? "Verifying..."
                  : playerVerified
                    ? "Verified"
                    : "Verify"}
              </button>
            </div>
          </div>

          {/* Player Information Display */}
          {playerVerified && verifiedPlayer && (
            <div className="bg-slate-800 border border-green-600 rounded-lg p-4">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {verifiedPlayer.photo ? (
                    <>
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/${verifiedPlayer.photo}`}
                        alt={verifiedPlayer.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-green-600"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          const fallback =
                            target.nextElementSibling as HTMLElement | null;
                          target.style.display = "none";
                          if (fallback) {
                            fallback.style.display = "flex";
                          }
                        }}
                      />
                      <div
                        className="w-16 h-16 rounded-full bg-slate-600 flex items-center justify-center border-2 border-green-600"
                        style={{ display: "none" }}
                      >
                        <UserIcon className="w-8 h-8 text-slate-300" />
                      </div>
                    </>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-600 flex items-center justify-center border-2 border-green-600">
                      <UserIcon className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium text-lg">
                    {verifiedPlayer.name}
                  </h3>
                  <p className="text-slate-300 text-sm">
                    Email: {verifiedPlayer.email}
                  </p>
                  {verifiedPlayer.phone && (
                    <p className="text-slate-300 text-sm">
                      Phone: {verifiedPlayer.phone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Agent ID */}
          <div>
            <BodySmallRegular className="text-gray-300 mb-2">
              Agent ID
            </BodySmallRegular>
            <div className="relative" ref={dropdownRef}>
              <div
                className={`
                relative flex items-center
                bg-gray-800/50 backdrop-blur-sm
                border rounded-lg
                transition-all duration-200 ease-in-out
                ${!watchAssignAgent ? "opacity-50" : ""}
                ${isDropdownOpen ? "border-blue-500 shadow-lg shadow-blue-500/20" : "border-gray-600"}
              `}
              >
                <div className="flex items-center justify-center w-12 h-12 text-gray-400">
                  <HashIcon className="w-5 h-5" />
                </div>

                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search agent by ID or name"
                  disabled={isLoading || !watchAssignAgent}
                  className="flex-1 bg-transparent text-white placeholder-gray-400 px-3 py-3 focus:outline-none disabled:cursor-not-allowed"
                  onFocus={() => {
                    if (agentsData && agentsData.length > 0) {
                      setIsDropdownOpen(true);
                    }
                  }}
                />

                <div className="flex items-center justify-center w-12 h-12 text-gray-400">
                  <ArrowDown
                    className={`w-5 h-5 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                  />
                </div>
              </div>

              {/* Loading indicator */}
              {isLoadingAgents && (
                <div className="absolute top-full left-0 right-0 bg-slate-700/95 backdrop-blur-sm border border-slate-600 rounded-lg mt-1 p-3 z-10">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-slate-300">
                      Searching agents...
                    </span>
                  </div>
                </div>
              )}

              {/* Dropdown */}
              {isDropdownOpen &&
                agentsData &&
                agentsData.length > 0 &&
                !agentVerified && (
                  <div className="absolute top-full left-0 right-0 bg-slate-700/95 backdrop-blur-sm border border-slate-600 rounded-lg mt-1 max-h-64 overflow-hidden z-20 shadow-xl">
                    <div className="max-h-64 overflow-y-auto">
                      {agentsData.map((agent: AgentSearchResult) => (
                        <div
                          key={agent.id}
                          onClick={() => handleAgentSelect(agent)}
                          className={`
                          p-4 cursor-pointer transition-all duration-150 ease-in-out
                          hover:bg-slate-600/80 hover:backdrop-blur-sm
                          border-b border-slate-600/50 last:border-b-0
                          group
                        `}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center group-hover:bg-slate-500 transition-colors">
                                <HashIcon className="w-5 h-5 text-slate-300" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium truncate">
                                  {agent.name}
                                </span>
                                <span className="text-blue-400 text-sm font-mono bg-blue-500/10 px-2 py-1 rounded">
                                  {agent.agentId}
                                </span>
                              </div>
                              <p className="text-slate-300 text-sm truncate mt-1">
                                {agent.email}
                              </p>
                              {agent.phone && (
                                <p className="text-slate-400 text-xs truncate">
                                  {agent.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* No results message */}
              {watchAssignAgent &&
                searchQuery &&
                !isLoadingAgents &&
                !agentVerified &&
                (!agentsData || agentsData.length === 0) && (
                  <div className="absolute top-full left-0 right-0 bg-slate-700/95 backdrop-blur-sm border border-slate-600 rounded-lg mt-1 p-4 z-10">
                    <div className="text-center">
                      <p className="text-slate-400 text-sm">No agents found</p>
                      <p className="text-slate-500 text-xs mt-1">
                        Try searching with a different term
                      </p>
                    </div>
                  </div>
                )}
            </div>

            {/* Validation message */}
            {watchAssignAgent &&
              !agentVerified &&
              searchQuery &&
              !isLoadingAgents && (
                <p className="mt-2 text-sm text-red-400 animate-pulse">
                  Please select an agent from the search results.
                </p>
              )}
          </div>

          {/* Agent Information Display */}
          {agentVerified && verifiedAgent && watchAssignAgent && (
            <div className="bg-slate-800 border border-green-600 rounded-lg p-4">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-slate-600 flex items-center justify-center border-2 border-green-600">
                    <HashIcon className="w-8 h-8 text-slate-300" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium text-lg">
                    {verifiedAgent.name}
                  </h3>
                  <p className="text-slate-300 text-sm">
                    Email: {verifiedAgent.email}
                  </p>
                  <p className="text-slate-300 text-sm">
                    Phone: {verifiedAgent.phone}
                  </p>
                  <p className="text-slate-300 text-sm">
                    Agent ID:
                    <span className="text-blue-400 font-medium ml-1">
                      {verifiedAgent.agentId}
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDeselectAgent}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
                >
                  Deselect
                </button>
              </div>
            </div>
          )}

          {/* Assign Agent Checkbox */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="assignAgent"
              {...register("assignAgent")}
              className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
              disabled={isLoading}
            />
            <label htmlFor="assignAgent" className="text-slate-300">
              <BodySmallMedium>Assign an Agent</BodySmallMedium>
            </label>
          </div>

          {/* Payment Method */}
          <div>
            <BodySmallRegular className="text-gray-300 mb-2">
              Payment Method*
            </BodySmallRegular>

            <Controller
              name="paymentMethod"
              control={control}
              rules={{ required: "Payment method is required" }}
              render={({ field }) => (
                <div className="grid grid-cols-3 gap-4">
                  {PAYMENT_METHODS.filter(
                    (method) => method.value !== "credit" || watchAssignAgent,
                  ).map((method) => {
                    const isSelected = field.value === method.value;
                    return (
                      <label
                        key={method.value}
                        className={`flex flex-col items-center p-3 rounded-lg cursor-pointer transition-colors
                ${
                  isSelected
                    ? "border-2 border-blue-500 bg-slate-600"
                    : "border border-slate-600 bg-slate-700 hover:bg-slate-600"
                }`}
                        onClick={() => field.onChange(method.value)}
                      >
                        <input
                          type="radio"
                          value={method.value}
                          checked={field.value === method.value}
                          onChange={() => field.onChange(method.value)}
                          disabled={isLoading}
                          className="sr-only"
                        />
                        <span className="text-white mt-2">{method.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            />

            {errors.paymentMethod && (
              <p className="mt-1 text-sm text-red-400">
                {errors.paymentMethod.message}
              </p>
            )}
          </div>

          {/* Currency and Exchange Rate Display */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <BodySmallRegular className="text-gray-300 mb-2">
                Currency*
              </BodySmallRegular>
              <div className="relative">
                <Controller
                  name="currency"
                  control={control}
                  rules={{ required: "Currency is required" }}
                  render={({ field }) => (
                    <>
                      <select
                        {...field}
                        className="w-full pl-8 pr-10 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-slate-600 appearance-none disabled:opacity-50"
                        disabled={isLoading || isLoadingForexRates}
                      >
                        {availableCurrencies.map((currency) => (
                          <option key={currency.value} value={currency.value}>
                            {currency.label}
                          </option>
                        ))}
                      </select>
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">
                        {getCurrencySymbol(field.value)}
                      </span>
                    </>
                  )}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ArrowDown className="w-5 h-5 text-slate-400" />
                </span>
              </div>
              {errors.currency && (
                <p className="mt-1 text-sm text-red-400">
                  {errors.currency.message}
                </p>
              )}
            </div>

            <div>
              <BodySmallRegular className="text-gray-300 mb-2">
                Foreign Exchange Rate*
              </BodySmallRegular>
              <CustomInput
                icon={<Calculator className="w-5 h-5 text-slate-400" />}
                placeholder="Enter exchange rate"
                type="number"
                name="exchangeRate"
                register={register}
                validation={{
                  required: "Exchange rate is required",
                  min: {
                    value: 0.01,
                    message: "Rate must be greater than 0",
                  },
                }}
                error={errors.exchangeRate}
                disabled={isLoading}
                step={0.001}
              />
            </div>
          </div>

          {/* Cash Amount and NPR Amount */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <BodySmallRegular className="text-gray-300 mb-2">
                Cash Amount to Exchange*
              </BodySmallRegular>
              <CustomInput
                icon={<Calculator className="w-5 h-5 text-slate-400" />}
                placeholder="Enter amount"
                type="number"
                name="cashAmount"
                register={register}
                validation={{
                  required: "Cash amount is required",
                  min: {
                    value: 0.01,
                    message: "Amount must be greater than 0",
                  },
                }}
                error={errors.cashAmount}
                disabled={isLoading}
              />
            </div>

            <div>
              <BodySmallRegular className="text-gray-300 mb-2">
                Final Amount (INR)
              </BodySmallRegular>
              <CustomInput
                icon={<span className="text-slate-400">₹</span>}
                placeholder="Calculated INR amount"
                type="number"
                name="nprAmount"
                register={register}
                disabled={true}
                error={errors.nprAmount}
              />
            </div>
          </div>
          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <PrimaryButton
              type="submit"
              disabled={isLoading || !playerVerified}
              className="px-8 py-3"
            >
              {isLoading ? "Processing..." : "Save"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};
